import clsx from "clsx";
import React from "react";
import {
  MenuProps,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MoreHoriz } from "@mui/icons-material";
import {
  Trash2,
  Share2,
  UserMinus,
  Pencil,
  FileText,
  Rows3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import {
  ProgramTemplateDto,
  ProgramTemplateType,
} from "@growth-machine-llc/stridist-api-client";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import ProgramTemplatesService from "../../services/ProgramTemplatesService";
import { COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY } from "./ProgramsTableScreen";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import { useOptimisticUpdateMutation } from "../../hooks/useOptimisticUpdateMutation";
import { ProgramTemplatesVm } from "@growth-machine-llc/stridist-api-client";
import { COACH_PROGRAM_CURRICULUM_ROUTE } from "../../routes/routes";
import { CreateProgramFromTemplateDialog } from "../dialog/CreateProgramFromTemplateDialog";
import { COACH_PROGRAMS_LIST_QUERY_KEY } from "../coach-programs/CoachProgramsListScreen";
import { useAnalytics } from "../../hooks/useAnalytics";
import { Menu } from "../menu/Menu";

const useStyles = makeStyles(() => ({
  root: {},
}));

export interface ProgramTemplateMenuProps
  extends Omit<MenuProps, "open" | "onClose"> {
  template: ProgramTemplateDto;
  onTemplateSelected?: () => void;
  buttonClassName?: string;
}

export function ProgramTemplateMenu(props: ProgramTemplateMenuProps) {
  const { className, template, onTemplateSelected, buttonClassName, ...other } =
    props;

  const s = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToastAlert } = useToastAlert();
  const [trackEvent] = useAnalytics();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
    React.useState(false);
  const [createProgramDialogOpen, setCreateProgramDialogOpen] =
    React.useState(false);

  const canEdit = template.canEdit;
  const isTeamShared = template.type === ProgramTemplateType.TEAM;

  const { mutate: deleteTemplate } = useMutation({
    mutationFn: ProgramTemplatesService.deleteProgramTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY],
      });
      showToastAlert("success", {
        message: "Program template deleted successfully.",
      });
    },
  });

  const { mutate: shareWithTeam } = useOptimisticUpdateMutation<
    void,
    Error,
    number,
    ProgramTemplatesVm
  >({
    mutationFn: ProgramTemplatesService.shareProgramTemplateWithTeam,
    queryKey: [COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY],
    successToastMessage: "Program template shared with team successfully.",
    optimisticUpdater: {
      updateFn: (oldData, templateId) => {
        if (!oldData?.programTemplates) return oldData;
        return ProgramTemplatesVm.fromJS({
          ...oldData,
          programTemplates: oldData.programTemplates.map((t) =>
            t.id === templateId ? { ...t, type: ProgramTemplateType.TEAM } : t,
          ),
        });
      },
    },
  });

  const { mutate: unshareFromTeam } = useOptimisticUpdateMutation<
    void,
    Error,
    number,
    ProgramTemplatesVm
  >({
    mutationFn: ProgramTemplatesService.unshareProgramTemplateFromTeam,
    queryKey: [COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY],
    successToastMessage: "Program template removed from team successfully.",
    optimisticUpdater: {
      updateFn: (oldData, templateId) => {
        if (!oldData?.programTemplates) return oldData;
        return ProgramTemplatesVm.fromJS({
          ...oldData,
          programTemplates: oldData.programTemplates.map((t) =>
            t.id === templateId
              ? { ...t, type: ProgramTemplateType.PRIVATE }
              : t,
          ),
        });
      },
    },
  });

  const { mutate: createProgram, isPending: isCreatingProgram } = useMutation({
    mutationFn: ProgramTemplatesService.useProgramTemplate,
    onSuccess: (slug) => {
      queryClient.invalidateQueries({
        queryKey: [COACH_PROGRAMS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY],
      });
      showToastAlert("success", {
        message: "Successfully created new program using template.",
      });
      navigate(COACH_PROGRAM_CURRICULUM_ROUTE.replace(":slug", slug));
    },
  });

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDelete = React.useCallback(() => {
    setAnchorEl(null);
    setConfirmDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = React.useCallback(() => {
    if (template.id) {
      deleteTemplate(template.id);
    }
    setConfirmDeleteDialogOpen(false);
  }, [template.id, deleteTemplate]);

  const handleCancelDelete = React.useCallback(() => {
    setConfirmDeleteDialogOpen(false);
  }, []);

  const handleCreateWithName = React.useCallback(() => {
    setAnchorEl(null);
    setCreateProgramDialogOpen(true);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setCreateProgramDialogOpen(false);
  }, []);

  const handleDialogConfirm = React.useCallback(
    (programName: string) => {
      if (template.id) {
        createProgram(
          { id: template.id, name: programName },
          {
            onSuccess: () => {
              setCreateProgramDialogOpen(false);
              trackEvent("Coach - Use Program Template", {
                name: programName,
                templateId: template.id,
              });
            },
          },
        );
      }
    },
    [template.id, createProgram, trackEvent],
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        aria-label="program template actions"
        className={buttonClassName}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        id={`template-menu-${template.id}`}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        className={clsx(s.root, className)}
        {...other}
      >
        <MenuItem onClick={handleCreateWithName} dense>
          <ListItemIcon>
            <FileText />
          </ListItemIcon>
          <ListItemText>Create program</ListItemText>
        </MenuItem>
        {canEdit && (
          <>
            {isTeamShared ? (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  if (template.id) unshareFromTeam(template.id);
                }}
                dense
              >
                <ListItemIcon>
                  <UserMinus />
                </ListItemIcon>
                <ListItemText>Remove from Team</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  if (template.id) shareWithTeam(template.id);
                }}
                dense
              >
                <ListItemIcon>
                  <Share2 />
                </ListItemIcon>
                <ListItemText>Share with Team</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete} dense>
              <ListItemIcon>
                <Trash2 />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      <ConfirmActionDialog
        title="Are you sure you want to delete this program template?"
        open={confirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <CreateProgramFromTemplateDialog
        open={createProgramDialogOpen}
        onClose={handleDialogClose}
        templateName={template.name}
        onConfirm={handleDialogConfirm}
        isPending={isCreatingProgram}
      />
    </>
  );
}
